import React, { useEffect, useMemo, useRef, useState } from "react";
import "../PrepaidComponents/Prepaid.scss";
import "../PrepaidInstance/InstanceForms.scss";
import {
  I2cBadge,
  I2cIcon,
  I2cFormatDate,
} from "@webcomponents/i2cwebcomponents/dist/react";
import ReviewAccordionRenderer from "../PrepaidComponents/ReviewSummary/ReviewAccordionRenderer";
import ReviewSummarySearch from "../../Components/ReviewSummarySearch/ReviewSummarySearch";
import ReviewSearchEmptyState from "../../Components/ReviewSummarySearch/ReviewSearchEmptyState";
import {
  canSearch,
  buildSearchableDataset,
  clearSearchHighlights,
  highlightMatchingElements,
  openAncestorAccordions,
} from "../PrepaidComponents/ReviewSummary/reviewSummaryUtils";
import SalesService from "../../Services/SalesService";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { getBadgeVariant } from "../../Utils/stringUtls";
import { extractKeyValues } from "../Users/UserProjects";
import { statuses } from "../SalesRequest/SingleSalePreview";
import { OverlayLoader } from "../../Components/Loader/Loader";
import { UserStateContext } from "../../Context/AuthContext";

const ViewFormSummary = () => {
  const { id: formId } = useParams();
  const type = window.location.pathname.split("/")[1];
  const appContext = UserStateContext();
  const clientUser = String(appContext.userRole) === "5" || appContext.roleName === "client";
  const navigate = useNavigate();
  
  // Search functionality
  const [reviewSummarySearch, setReviewSummarySearch] = useState("");
  const containerRef = useRef(null);
  const isSearchActiveRef = useRef(false);
  const [searchStatus, setSearchStatus] = useState({
    isActive: false,
    hasResults: true,
  });
  
  useEffect(() => {
    handleAccordion();
  }, []);

  function handleAccordion() {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleShow = (event) => {
      if (isSearchActiveRef.current) return;

      const accordion = container.querySelectorAll("i2c-accordion");
      for (let i = 0; i < accordion.length; i++) {
        if (accordion[i] !== event.target) {
          accordion[i].open = false;
        }
      }
    };

    container.addEventListener("i2c-show", handleShow);
    return () => container.removeEventListener("i2c-show", handleShow);
  }

  const fetchSingleSale = async () => {
    if (formId) {
      const res = await SalesService.fetchSale(formId);
      return res.data.data[0];
    }
    return null;
  };

  // Fetch sessions data using React Query hook
  const queryKey = ["form-review", formId];

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const {
    data: singleForm,
    refetch,
    isFetching,
    isLoading,
  } = useQuery(queryKey, fetchSingleSale, {
    enabled: !!formId, // Only fetch if formId is present,
    staleTime: Infinity,
  });

  // Accordion sections configuration for view-only mode
  const accordionSections = useMemo(
    () => [
      { sectionId: "0", summary: "Project & Client Information" },
      { sectionId: "1", summary: "Program Information" },
      { sectionId: "2", summary: "Card Network And Bin Info" },
      { sectionId: "3", summary: "Interfaces and Components" },
      { sectionId: "4", summary: "Value Added Services" },
      { sectionId: "5", summary: "Connectivity & File Exchange" },
      { sectionId: "6", summary: "Web Services API" },
      { sectionId: "7", summary: "Cooperative Authentication" },
      { sectionId: "8", summary: "Settlement & Reconciliation" },
      { sectionId: "9", summary: "Additional Comments" },
      { sectionId: "10", summary: "Use Case Document" },
      { sectionId: "11", summary: "Client Signoff" },
      { sectionId: "12", summary: "I2C Internal Stakeholder Review & Sign Off" },
    ],
    [],
  );

  const css = `
  .review-summary-sticky {
    position: sticky;
    top: var(--i2c-spacing-20);
    z-index: 50;
    background: var(--bg-secondary_alt);
    padding-top: var(--i2c-spacing-2);
    padding-bottom: var(--i2c-spacing-2);
    box-shadow: 0 -60px 0 60px var(--bg-secondary_alt);
  }

  .review-form-header {
		color: var(--colors-foreground-fg-primary-900, #121521);
		padding-top: var(--i2c-spacing-3);
		h2 {
			font-size: var(--i2c-font-size-display-xs);
			color: var(--text-primary-900);
			font-family: Inter;
			font-style: normal;
			font-weight: var(--i2c-font-weight-bold);
			line-height: var(--i2c-line-height-1-33);
			
		}
	}

  .review-summary-step-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--i2c-spacing-4);
    flex-wrap: wrap;
    margin: 0 0 var(--i2c-spacing-6) 0;
  }

  @media (max-width: 768px) {
    .review-summary-step-header {
      align-items: flex-start;
      flex-direction: column;
    }

    .review-summary-search i2c-input {
      width: 100%;
    }

    .review-summary-search {
      flex-basis: auto;
      max-width: 100%;
      min-width: 100%;
      width: 100%;
    }
  }

  .accordion-container{
    margin-bottom:0px;
  }

  .accordion-container::part(base) {
    padding:0 var(--i2c-spacing-6) 0 var(--i2c-spacing-6) ;
  }

  .accordion-container::part(summary) {
    font-family: Inter;
    font-size: var(--i2c-font-size-text-lg);
    font-weight: var(--i2c-font-weight-medium);
    line-height: var(--i2c-line-height-1-5);
  }
  
  .accordion-container::part(header) {
    border-bottom: 2px solid var(--border-secondary);
    margin-bottom: var(--i2c-spacing-1);
    padding:var(--i2c-spacing-2) 0px var(--i2c-spacing-6) 0px; 
    margin-bottom: var(--i2c-spacing-4);
    
  }

  .accordion-container::part(content) {
    border-bottom: 2px solid var(--border-secondary);
  }

  .border-less-container::part(header){
    padding-top:var(--i2c-spacing-4);
    border-top: 0;
  }

  .sale-overview-parent{
    padding: var(--i2c-spacing-6);
    background: var(--bg-primary_alt);
    border-bottom: 2px solid var(--border-secondary);    
  } 

  .single-sale-inner1{
    display: flex;
    justify-content: space-between;
    @media (max-width: 768px) {
      margin-bottom: var(--i2c-spacing-6);
  }
    @media (max-width: 466px) {
      flex-direction: column;
    }
  }

  .single-sale-name{
    display:flex;
    align-items: center;
    gap: var(--i2c-spacing-2);
    flex-wrap: wrap;
    .project-name{
      font-size: var(--i2c-font-size-text-xl);
      font-weight: var(--i2c-font-weight-bold);
      line-height: var(--i2c-line-height-1-3);
    }
  }

  @media (max-width: 767px) {
    .single-sale-name {
      flex-direction: column-reverse;
      align-items: flex-start;
      gap: var(--i2c-spacing-2);
    }
  }

  .single-sale-inner-child{
    display:flex;
    align-items:center;
    gap:var(--i2c-spacing-3);
    min-width:250px;
  }
  `;

  // Memoize parsed form data to prevent infinite loops
  const formData = useMemo(
    () => {
      if (!singleForm?.form_data) return {};
      return typeof singleForm.form_data === 'string' 
        ? JSON.parse(singleForm.form_data) 
        : singleForm.form_data;
    },
    [singleForm?.form_data],
  );
  
  const signatureFormData = useMemo(
    () => {
      if (!singleForm?.signature) return {};
      return typeof singleForm.signature === 'string' 
        ? JSON.parse(singleForm.signature) 
        : singleForm.signature;
    },
    [singleForm?.signature],
  );
  
  // Parse I2C stakeholder sign-off data if available
  const i2cStakeHolderSignOff = useMemo(
    () => {
      if (!singleForm?.stakeholder_signoff) return {};
      return typeof singleForm.stakeholder_signoff === 'string' 
        ? JSON.parse(singleForm.stakeholder_signoff) 
        : singleForm.stakeholder_signoff;
    },
    [singleForm?.stakeholder_signoff],
  );
  
  const showFormSignature = clientUser
    ? true
    : singleForm?.form_status_id === 4;

  // Build searchable dataset
  const searchableData = useMemo(
    () => buildSearchableDataset(formData, accordionSections),
    [formData, accordionSections],
  );

  // Search effect
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    clearSearchHighlights(container);

    const normalizedQuery = reviewSummarySearch.trim().toLowerCase();
    const canRunSearch = canSearch(normalizedQuery);
    isSearchActiveRef.current = canRunSearch;

    if (!canRunSearch) {
      setSearchStatus({ isActive: false, hasResults: true });
      return;
    }

    const dataSectionIds = searchableData
      .filter(({ searchableText }) => searchableText.includes(normalizedQuery))
      .map(({ sectionId }) => sectionId);

    const accordionMatches = Array.from(
      container.querySelectorAll("[data-review-section]"),
    ).filter((accordion) => {
      const sectionId = accordion.dataset.reviewSection;
      const hasDataMatch = dataSectionIds.includes(sectionId);
      const hasRenderedMatch = (accordion.textContent || "")
        .toLowerCase()
        .includes(normalizedQuery);

      return hasDataMatch || hasRenderedMatch;
    });

    setSearchStatus({
      isActive: true,
      hasResults: accordionMatches.length > 0,
    });

    if (!accordionMatches.length) return;

    const [firstMatch] = accordionMatches;
    Array.from(container.querySelectorAll("[data-review-section]")).forEach(
      (accordion) => {
        accordion.open = accordionMatches.includes(accordion);
      },
    );

    accordionMatches.forEach((accordion) => {
      accordion.classList.add("review-search-section-match");
    });

    const highlightTimeout = setTimeout(() => {
      clearSearchHighlights(container);
      accordionMatches.forEach((accordion) => {
        accordion.classList.add("review-search-section-match");
      });

      const highlightedElements = highlightMatchingElements(
        accordionMatches,
        normalizedQuery,
      );
      const scrollTarget = highlightedElements[0] || firstMatch;
      openAncestorAccordions(scrollTarget);
      scrollTarget.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);

    return () => clearTimeout(highlightTimeout);
  }, [reviewSummarySearch, searchableData]);

  // Early returns after all hooks
  if (Object.keys(formData)?.length === 0) return null;
  if (isFetching || isLoading)
    return <OverlayLoader style={{ height: `calc(100vh - 125px)` }} />;

  const variant = getBadgeVariant(formData?.form_status_id);
  const keysToExtract = [
    "pd_client_name",
    "pi_program_type",
    "pi_program_name",
    "pd_region",
    "pd_team_avail",
    "pd_go_live",
    "ci_businessName",
    "ci_companyPhone",
    "cp_fi_fi_name",
    "cp_fi_phone_num",
  ];

  const extractedData = extractKeyValues([...formData], keysToExtract);
  const saleData = {};
  extractedData.forEach((obj) => {
    const [key, value] = Object.entries(obj)[0];
    saleData[key] = value;
  });

  return (
    <React.Fragment>
      <style>{css}</style>
      <div className="layout_aside_logged_user user-form-container">
        <div className="review-summary-sticky">
          <div onClick={() => navigate(-1)} className="go-back-button">
            <I2cIcon
              name="cs-linear-chevron-left"
              label="icon"
              library="default"
              className="go-back-button-icon"
            />
            Back
          </div>
          <div className="review-form-header">
            <div className="review-summary-step-header">
              <h2>Review Summary</h2>
              <ReviewSummarySearch
                value={reviewSummarySearch}
                onSearchChange={setReviewSummarySearch}
              />
            </div>
          </div>
        </div>
        <div
          ref={containerRef}
          className="accordion-group-example"
          style={{ width: "100%" }}
        >
          <div i2c-grid="grid gap-sm">
            <div i2c-grid="12">
              {searchStatus.isActive && !searchStatus.hasResults && (
                <ReviewSearchEmptyState />
              )}
              <div className=" users-listing-section-single-user">
                <div className="sale-overview-parent">
                  <div className="single-sale-inner1">
                    {/* Project Details */}
                    <div className="single-sale-inner-child">
                      <div className="single-sale-detail">
                        {/* Project Name and status */}
                        <div className="single-sale-name">
                          <strong className="project-name">
                            {saleData?.pi_program_name ?? "-"}
                            {extractedData.pd_client_name}
                          </strong>
                          <I2cBadge variant={`light-${variant}`} size="small">
                            {statuses[singleForm.form_status_id - 1]}
                          </I2cBadge>
                        </div>
                        {/* User Name & Creation Date */}
                        <div className="project-user-name-date mb-12">
                          <span>By {singleForm.added_by}</span>
                          <div className="date-holder">
                            <I2cFormatDate
                              month="short"
                              day="numeric"
                              year="numeric"
                              date={singleForm.created_at}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Use shared ReviewAccordionRenderer component */}
              <ReviewAccordionRenderer
                formData={formData}
                signatureFormData={signatureFormData}
                i2cStakeHolderSignOff={i2cStakeHolderSignOff}
                type={type}
                editable={false}
                showClientSignoff={showFormSignature}
                clientUser={clientUser}
                accordionSections={accordionSections}
                variant="borderless"
              />
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ViewFormSummary;
